import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, MenuItem, Checkbox, FormControlLabel, Alert
} from '@mui/material';
import { formatCurrency, parseCurrency } from '../utils/currencyUtils';

const CustomLabel = ({ title, required = false }) => (
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, color: '#2c3e50', fontSize: '0.9rem' }}>
        {title} {required && <span style={{ color: 'red' }}>*</span>}
    </Typography>
);

const CANCELLATION_OPTIONS = [
    { value: 'NO_PAYMENT', label: 'Chưa thanh toán (Không hoàn tiền)' },
    { value: 'REFUND', label: 'Đã thanh toán - Có hoàn tiền' },
    { value: 'NO_REFUND', label: 'Đã thanh toán - Không hoàn tiền (Phạt 100%)' },
];

const CancellationDialog = ({ open, onClose, booking, onSubmit }) => {
    const [cancelType, setCancelType] = useState('NO_PAYMENT');
    const [reason, setReason] = useState('');
    const [received, setReceived] = useState(0);
    const [refund, setRefund] = useState(0);
    
    // State mới: Xác định xem có hoàn tiền ngay lập tức không
    const [isRefundedNow, setIsRefundedNow] = useState(false); 
    const [billFile, setBillFile] = useState(null);

    useEffect(() => {
        if (open && booking) {
            setReason('');
            setBillFile(null);
            setIsRefundedNow(false); // Mặc định là chưa hoàn tiền ngay (để an toàn)
            
            const totalPrice = parseFloat(booking.total_price) || 0;
            
            if (booking.payment_status === 'PAID') {
                setCancelType('REFUND');
                setReceived(totalPrice);
                setRefund(0); 
            } else if (booking.payment_status === 'DEPOSIT') {
                setCancelType('REFUND');
                setReceived(0);
                setRefund(0);
            } else {
                setCancelType('NO_PAYMENT');
                setReceived(0);
                setRefund(0);
            }
        }
    }, [open, booking]);

    const handleTypeChange = (newType) => {
        setCancelType(newType);
        if (newType === 'NO_PAYMENT') {
            setReceived(0);
            setRefund(0);
            setIsRefundedNow(false);
        } else if (newType === 'NO_REFUND') {
            setRefund(0);
            setIsRefundedNow(false);
            if (booking.payment_status === 'PAID') {
                setReceived(parseFloat(booking.total_price) || 0);
            }
        }
    };

    const handleSubmit = () => {
        if (!reason.trim()) {
            alert("Vui lòng nhập lý do hủy!");
            return;
        }

        // Logic xác định trạng thái hoàn tiền
        let refundStatus = 'NONE';
        
        if (cancelType === 'REFUND') {
            if (isRefundedNow) {
                if (!billFile) {
                    alert("Bạn đã chọn 'Đã hoàn tiền ngay', vui lòng tải lên Bill chuyển khoản!");
                    return;
                }
                refundStatus = 'COMPLETED';
            } else {
                refundStatus = 'PENDING'; // Treo công nợ hoàn tiền
            }
        }

        onSubmit({
            cancellation_type: cancelType,
            cancellation_reason: reason,
            amount_received: received,
            refund_amount: refund,
            booking_status: 'CANCELLED',
            refund_status: refundStatus, // Gửi trạng thái mới lên
            cancelled_at: new Date().toISOString(),
            refund_bill_image: isRefundedNow ? billFile : null // Chỉ gửi ảnh nếu đã hoàn
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: '#d32f2f', color: 'white', fontWeight: 'bold' }}>
                XÁC NHẬN HỦY BOOKING: {booking?.booking_code}
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    
                    <Box>
                        <CustomLabel title="Phân loại trường hợp hủy" required />
                        <TextField
                            select size="small" fullWidth
                            value={cancelType}
                            onChange={(e) => handleTypeChange(e.target.value)}
                        >
                            {CANCELLATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box>
                        <CustomLabel title="Lý do hủy" required />
                        <TextField
                            size="small" fullWidth multiline rows={1}
                            value={reason} onChange={(e) => setReason(e.target.value)}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box flex={1}>
                            <CustomLabel title="Thực tế đã thu (VND)" />
                            <TextField
                                size="small" fullWidth
                                value={formatCurrency(received)}
                                onChange={(e) => setReceived(parseCurrency(e.target.value))}
                                disabled={cancelType === 'NO_PAYMENT'}
                            />
                        </Box>
                        <Box flex={1}>
                            <CustomLabel title="Số tiền hoàn lại (VND)" />
                            <TextField
                                size="small" fullWidth
                                value={formatCurrency(refund)}
                                onChange={(e) => setRefund(parseCurrency(e.target.value))}
                                disabled={cancelType === 'NO_PAYMENT' || cancelType === 'NO_REFUND'}
                                sx={{ '& input': { color: refund > 0 ? '#d32f2f' : 'inherit', fontWeight: 'bold' } }}
                            />
                        </Box>
                    </Box>

                    {/* LOGIC MỚI: CHỌN HOÀN TIỀN NGAY HAY SAU */}
                    {cancelType === 'REFUND' && refund > 0 && (
                        <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #ffcc80' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={isRefundedNow} 
                                        onChange={(e) => setIsRefundedNow(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={<Typography fontWeight="bold" color="primary">Tôi đã chuyển khoản hoàn tiền ngay bây giờ</Typography>}
                            />
                            
                            {!isRefundedNow ? (
                                <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                                    Hệ thống sẽ ghi nhận trạng thái <b>"Chờ hoàn tiền"</b>. Bạn có thể cập nhật Bill sau.
                                </Alert>
                            ) : (
                                <Box sx={{ mt: 2 }}>
                                    <CustomLabel title="Bill chuyển khoản hoàn tiền" required />
                                    <TextField 
                                        type="file" size="small" fullWidth 
                                        inputProps={{ accept: "image/*" }}
                                        onChange={(e) => setBillFile(e.target.files[0])}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                    
                    <Box sx={{ bgcolor: '#ffebee', p: 1, borderRadius: 1, textAlign: 'center', border: '1px dashed #d32f2f' }}>
                        <Typography variant="subtitle2">Doanh thu giữ lại (Phạt hủy):</Typography>
                        <Typography variant="h6" color="error" fontWeight="bold">
                            {formatCurrency(received - refund)} VND
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">Thoát</Button>
                <Button onClick={handleSubmit} variant="contained" color="error" sx={{ fontWeight: 'bold' }}>
                    Xác nhận Hủy
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancellationDialog;